import { CommonModule } from '@angular/common';
import { afterNextRender, Component, ElementRef, inject, OnDestroy, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIslandComponent, RaIslandRingComponent, uiPx } from 'ra-ui';
import { map, tap } from 'rxjs';
import { UploadItem, UploadProgressService } from '../../services/upload-progress.service';
import { RaIconComponent } from '../ra-icon/ra-icon.component';

/** Uploads separados pelo papel na UI: ativos numa pílula agregada, erros em pílulas próprias. */
interface UploadView {
    active: UploadItem[];
    errors: UploadItem[];
}

@Component({
    selector: 'ra-upload-progress-bar',
    standalone: true,
    imports: [CommonModule, RaIconComponent, RaIslandComponent, RaIslandRingComponent, TranslatePipe],
    templateUrl: './ra-upload-progress-bar.component.html',
    styleUrl: './ra-upload-progress-bar.component.scss',
})
export class RaUploadProgressBarComponent implements OnDestroy {
    private _uploadProgressService = inject(UploadProgressService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _resizeObserver?: ResizeObserver;

    constructor() {
        // Publica a altura da pilha em --island-upload-height (no <body>) para os
        // toasts pill empilharem ACIMA dos uploads em vez de cobri-los — os dois
        // ancoram no mesmo centro do rodapé.
        afterNextRender(() => {
            this._resizeObserver = new ResizeObserver(() => this._publishDockHeight());
            this._resizeObserver.observe(this._host.nativeElement);
            this._publishDockHeight();
        });
    }

    ngOnDestroy(): void {
        this._resizeObserver?.disconnect();
        document.body.style.removeProperty('--island-upload-height');
    }

    private _publishDockHeight(): void {
        const height = this._host.nativeElement.offsetHeight;

        if (height > 0) {
            // +8 (medida de desenho) de folga entre a pilha de upload e o toast acima dela.
            document.body.style.setProperty('--island-upload-height', `${height + uiPx(8)}px`);
        } else {
            document.body.style.removeProperty('--island-upload-height');
        }
    }

    /** Lista de arquivos aberta (só existe com 2+ uploads ativos). */
    public expanded = signal(false);

    public view$ = this._uploadProgressService.uploads$.pipe(
        map(
            (uploads): UploadView => ({
                active: uploads.filter(
                    upload =>
                        upload.status === 'compressing' ||
                        upload.status === 'uploading' ||
                        upload.status === 'saving'
                ),
                errors: uploads.filter(upload => upload.status === 'error'),
            })
        ),
        // Um lote novo sempre começa recolhido.
        tap(view => {
            if (view.active.length <= 1) this.expanded.set(false);
        })
    );

    /**
     * Fase agregada, na ordem em que acontecem: comprimir → enviar → gravar no
     * documento. A pílula fica na fase mais atrasada do lote, então só anuncia
     * "salvando" quando todos os arquivos já subiram.
     */
    public phaseOf(active: UploadItem[]): 'compressing' | 'uploading' | 'saving' {
        if (active.some(upload => upload.status === 'compressing')) return 'compressing';
        if (active.some(upload => upload.status === 'uploading')) return 'uploading';
        return 'saving';
    }

    /** Rótulo da fase exibido na pílula. */
    public phaseLabelKey(phase: 'compressing' | 'uploading' | 'saving'): string {
        return `shared.uploadProgressBar.${phase}Label`;
    }

    /** Progresso agregado: média simples dos uploads ativos. */
    public aggregateProgress(active: UploadItem[]): number {
        if (active.length === 0) return 0;
        const total = active.reduce((sum, upload) => sum + upload.progress, 0);
        return Math.round(total / active.length);
    }

    /** Ícone do tipo de mídia; lote misto cai no genérico de upload. */
    public mediaIcon(active: UploadItem[]): string {
        const mediaTypeIcons: Record<string, string> = {
            image: 'gallery',
            'group-image': 'grid-2',
            video: 'video-play',
        };

        const first = active[0]?.mediaType;
        if (!first || !mediaTypeIcons[first]) return 'document-upload';

        const uniform = active.every(upload => upload.mediaType === first);
        return uniform ? mediaTypeIcons[first] : 'document-upload';
    }

    public toggleExpanded(): void {
        this.expanded.update(value => !value);
    }

    public removeUpload(id: string): void {
        this._uploadProgressService.removeUpload(id);
    }
}
